import type { AppTemplate } from '@morph/spec';
/**
 * The hub — the section Joaquín asked for on 2026-08-31, in his words:
 *
 * > *"we need a templates / hub / whatever where we store stuff that we liked …
 * > so our proof (which is you and codex recreating with our app each of this
 * > and TESTING each part) would be in that section where we can see each"*
 *
 * Task 0149. The proof lives IN the product: each canon app the bar names,
 * rebuilt in Morph, openable from here, with its tested state shown — read
 * from `acceptance-map.json`, which the build emits from the SAME rows
 * `scripts/acceptance-coverage.mjs` prints as prose. One computation, two
 * presentations; a hub keeping its own copy of the map would drift the way
 * every duplicated list in this repo has (`VISUAL_KINDS` is the latest).
 *
 * Blind run 1 (e2e/blind/runs/2026-08-31-first.md) chose the door: the driver
 * that failed to find any proof surface said it expected one behind the Morph
 * wordmark — *"where I'd most expect a home/showcase to hang"* — which was
 * *"clickable-looking but dead"*. The wordmark now opens this.
 *
 * In its own file, injected rather than reaching for `main.ts` state, for the
 * reason `template-seed.ts` states about itself: a function reachable only
 * through a button is a function no unit test can reach. Everything this
 * renders is decided by its inputs.
 */

/** One capability row of `acceptance-map.json` — the JSON twin of the rows
 *  the coverage script prints. */
export interface MapCapability {
  readonly cap: string;
  readonly state: 'driven' | 'partial' | 'uncovered' | 'unbacked' | 'UNMAPPED';
  readonly detail: string;
}
export interface MapApp {
  readonly app: string;
  readonly capabilities: readonly MapCapability[];
}
export interface AcceptanceMap {
  readonly counts: {
    readonly driven: number; readonly partial: number;
    readonly unbacked: number; readonly none: number; readonly total: number;
  };
  readonly apps: readonly MapApp[];
  readonly problems: readonly string[];
}

/**
 * One layout the person kept, ready to reopen. @Codex, P1 on `4f1ea1b`, and
 * it is the amendment's own second clause: *"we need a … hub … where we
 * store stuff that we liked"* — the proof shelf was built and the LIKED
 * shelf was not, so a kept suggestion or a rearranged lens was retained by
 * the revision contract and findable nowhere. Display-ready: the host owns
 * the specs and the routing; this renderer stays pure.
 */
export interface HubKeptEntry {
  /** Identifies the entry to the host on open — collision-free, never the
   *  timestamp: two tabs can keep within one millisecond (@Codex, P2 on
   *  `65952e8`), and an ambiguous open is a wrong layout wearing a right
   *  label. `at` stays for display and sorting. */
  readonly id: string;
  readonly at: number;
  /** 'kept' (a suggestion) or 'rearranged' (the person's own drop). */
  readonly what: string;
  readonly poolLabel: string;
}

/** A composed app as the hub shows it: what a person named it, and what it is
 *  made of, which is the only description that is true without opening it. */
export interface HubComposedEntry {
  readonly id: string; readonly name: string; readonly of: string;
}

/** Where “Open” lands, injected by the host. `undefined` = nothing to open
 *  yet, and the card says so instead of growing a dead button. */
export interface HubDestination {
  readonly poolId: string;
  readonly view?: 'auto' | 'document';
  /** The NAMED APP LOOK the rebuild opens in (decision 0055) — the generator
   *  is asked for it by name, and the reproduction wears the app's own chrome
   *  rather than Morph's. Absent: Morph's generated presentation over that
   *  pool, which is what every rebuild was until 0170. */
  readonly template?: AppTemplate;
  /** One sentence of orientation shown under the app name — what you are
   *  looking at once it opens, in the person's terms. */
  readonly note: string;
}

/**
 * The states, in words a person reads — not glyphs alone. Blind run 1's
 * legibility findings (task 0154) were one long complaint about surfaces that
 * explain themselves only to people who already believe; a proof section that
 * greeted its first visitor with unlabeled dots would be the same defect,
 * freshly built.
 */
/**
 * "GUARDED", never "tested" — @Codex, P2 on `4f1ea1b`. The map this page
 * renders is `acceptance-coverage.mjs --json`, whose own footer says what it
 * is: every claim names a guard THAT EXISTS, and nothing in it verifies the
 * guard PASSES — the gate is what runs the harnesses, and this page renders
 * the map, not the gate's latest run. The distinction was found while the
 * gate could not acquire a runner at all (08-27 to 09-01) and it survives
 * the gate's recovery unchanged: a checkmark reading "tested" would publish
 * a green word THIS BUILD cannot back, whatever CI said about an earlier
 * one. "Guarded" is the exact strength of the fact on hand.
 */
const STATE_COPY: Record<MapCapability['state'], { mark: string; word: string }> = {
  driven: { mark: '✓', word: 'guarded' },
  partial: { mark: '◐', word: 'partly guarded' },
  uncovered: { mark: '○', word: 'not built yet' },
  // Both mean the map and the repo disagree. The build refuses to emit a map
  // with problems, so these should be unreachable here — rendered honestly
  // anyway, because "should be unreachable" is how gaps stay invisible.
  unbacked: { mark: '!', word: 'claim without a guard' },
  UNMAPPED: { mark: '!', word: 'claim without a guard' },
};

const esc = (s: string): string => s
  .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;');

/** A stable hook for harnesses AND a class for style — ids stay off the cards
 *  so the surface never depends on there being exactly one hub. */
const appSlug = (app: string): string => app.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const KEPT_WORDS: Record<string, string> = {
  kept: 'a suggestion you kept',
  rearranged: 'your own arrangement',
  /** Notion's star, under the look: the page itself, on this shelf. */
  starred: 'a page you starred',
};

/** The liked shelf. Every entry openable; an empty shelf says how it fills,
 *  because a person who has kept nothing yet is exactly who is reading it. */
/**
 * A COMPOSED APP on the hub — a seventh door that nobody hard-coded.
 *
 * Modelled on `renderKept` deliberately: a kept layout is already a stored,
 * openable thing the hub lists, so a composed app is the same shape with a
 * different noun. The alternative was a second mechanism for "things a person
 * made", and two doors that disagree about what open means is the defect this
 * file's own comments keep naming.
 *
 * The FORM is the answer to a question asked on 2026-09-07 — can a person
 * create an app? Until this, no: `LOOKS` is a static six and nothing authored
 * it. Three choices make one, because three is what composition needs at its
 * smallest: which app it is BASED on, which PART to replace, and whose part to
 * put there.
 */
function renderComposed(
  made: readonly HubComposedEntry[],
  bases: readonly string[],
  parts: readonly string[],
): string {
  const items = made.map((m) => '<li class="hub-made-row">'
    + `<span class="hub-made-name">${esc(m.name)}</span>`
    + `<span class="hub-made-of">${esc(m.of)}</span>`
    + `<button class="hub-open hub-made-open" type="button" data-hub-composed="${esc(m.id)}">`
    + 'Open</button>'
    + `<button class="hub-made-forget" type="button" data-hub-composed-forget="${esc(m.id)}">`
    + `Forget</button></li>`).join('');
  const opts = (xs: readonly string[]) => xs.map((x) =>
    `<option value="${esc(x)}">${esc(x)}</option>`).join('');
  /* THE BASES INCLUDE WHAT A PERSON HAS ALREADY MADE — task `0237`. Composing
     onto a composed app is how more than one part is reached, and it needs no
     new gesture: "Notion with Fibery's nav", then that with Coda's topbar.
     Offered by id and shown by name, because the id is what the record stores
     and the name is what the person called it. */
  const baseOpts = opts(bases)
    + made.map((m) => `<option value="${esc(m.id)}">${esc(m.name)}</option>`).join('');
  return '<section class="hub-made" aria-label="Apps you made">'
    + '<h3 class="hub-made-head">Apps you made</h3>'
    + (items
      ? `<ul class="hub-made-list">${items}</ul>`
      : '<p class="hub-made-none">None yet. Make one below — it opens beside the six.</p>')
    + '<form class="hub-compose" data-hub-compose>'
      + '<label class="hub-compose-f">Based on'
      + `<select data-compose-base>${baseOpts}</select></label>`
      + '<label class="hub-compose-f">Take its'
      + `<select data-compose-part>${opts(parts)}</select></label>`
      + '<label class="hub-compose-f">from'
      + `<select data-compose-from>${baseOpts}</select></label>`
      + '<label class="hub-compose-f">Call it'
      + '<input data-compose-name type="text" placeholder="My app"></label>'
      + '<button class="hub-open" type="submit" data-hub-compose-create>Create</button>'
    + '</form></section>';
}

function renderKept(kept: readonly HubKeptEntry[]): string {
  const items = kept.map((k) => {
    const when = new Date(k.at).toLocaleDateString();
    return '<li class="hub-kept-row">'
      + `<span class="hub-kept-what">${esc(KEPT_WORDS[k.what] ?? k.what)}</span>`
      + `<span class="hub-kept-where">in ${esc(k.poolLabel)} · ${esc(when)}</span>`
      + `<button class="hub-open hub-kept-open" type="button" data-hub-kept="${esc(k.id)}">`
      + 'Open</button></li>';
  }).join('');
  return '<section class="hub-kept" aria-label="Kept layouts">'
    + '<h3 class="hub-kept-head">Kept layouts</h3>'
    + (kept.length
      ? `<ul class="hub-kept-list">${items}</ul>`
      : '<p class="hub-kept-none">Nothing kept yet — Keep a suggestion, or '
        + 'rearrange a layout by dragging, and it lands here.</p>')
    + '</section>';
}

export function renderHub(
  map: AcceptanceMap,
  destinations: Readonly<Record<string, HubDestination>>,
  kept: readonly HubKeptEntry[] = [],
  made: readonly HubComposedEntry[] = [],
  bases: readonly string[] = [],
  parts: readonly string[] = [],
): string {
  const cards = map.apps.map((entry) => {
    const dest = destinations[entry.app];
    const done = entry.capabilities.filter((c) => c.state === 'driven').length;
    const rows = entry.capabilities.map((c) => {
      const s = STATE_COPY[c.state] ?? STATE_COPY.UNMAPPED;
      return `<li class="hub-cap hub-cap-${esc(c.state)}">`
        + `<span class="hub-mark" aria-hidden="true">${s.mark}</span>`
        + `<span class="hub-capname">${esc(c.cap)}</span>`
        + `<span class="hub-state">${s.word}</span></li>`;
    }).join('');
    const foot = dest
      ? `<button class="hub-open" type="button" data-hub-open="${esc(entry.app)}">`
        + `Open the ${esc(entry.app)} rebuild</button>`
      : '<p class="hub-open-none">Not started — nothing to open yet.</p>';
    return `<article class="hub-card hub-card-${appSlug(entry.app)}">`
      + `<h3 class="hub-app">${esc(entry.app)}</h3>`
      + `<p class="hub-note">${esc(dest?.note ?? 'This rebuild has not begun.')}</p>`
      + `<p class="hub-tally">${done} of ${entry.capabilities.length} capabilities guarded</p>`
      + `<ul class="hub-caps">${rows}</ul>${foot}</article>`;
  }).join('');

  /**
   * The thesis is DERIVED, not asserted — and it claims OPENABLE, never
   * "rebuilt". Two @Codex findings, one day apart, each about a word this
   * header could not back: on `4aa3b1f` it said every listed app "has been
   * rebuilt" while Fibery's card said its rebuild had not begun; the derived
   * version then said "rebuilt" for anything with a destination, and on
   * `5d1673b` Fabric had a destination, 6 of 7 driven and a partial — so the
   * header still contradicted the card under it. The bar's own test is
   * reproduction, complete, through the blind and visual gates, and no
   * tally on this shelf measures those. So the header claims exactly what
   * having a destination proves — you can open it — and points at the
   * cards' tallies for how far each rebuild has actually got.
   */
  const started = map.apps.filter((entry) => destinations[entry.app]).length;
  const opener = started === map.apps.length
    ? 'Each of these apps is being rebuilt inside Morph and is openable now'
    : `${started} of ${map.apps.length} of these apps are being rebuilt inside `
      + 'Morph and are openable now; the rest are named because they are next';
  const thesis = `${opener} — each card counts the capabilities a written, `
    + 'gate-run guard stands behind. Open one and use it: every layout in it '
    + 'is generated, not templated. This list grows; the point is that it can.';
  return '<header class="hub-head"><h2>What Morph replaces</h2>'
    + `<p class="hub-thesis">${thesis}</p></header>`
    + `<div class="hub-grid">${cards}</div>`
    + renderKept(kept)
    + renderComposed(made, bases, parts);
}

/** The honest failure surface. The build refuses to emit a wrong map, so the
 *  usual reason to see this is a served page whose fetch failed — say that,
 *  do not render an empty proof section that looks like "nothing is tested".
 *
 *  The liked shelf renders anyway — @Codex, P2 on `6fd9613`: kept layouts
 *  live in this browser and owe nothing to the map, and a fetch failure that
 *  hid them made every saved layout invisible and unopenable.
 *
 *  And so do the DOORS — @Codex, P1 on `b0af641`: the copy pointed at "the
 *  collection bar above", which a named app look hides, so a person who left
 *  Obsidian by its ribbon while the map failed to load had no way into any
 *  rebuild at all. The destinations owe nothing to the map either: a door is
 *  the host's fact, the tally is the map's, and only the tally is missing. */
export function renderHubFailure(
  why: string,
  destinations: Readonly<Record<string, HubDestination>> = {},
  kept: readonly HubKeptEntry[] = [],
): string {
  const doors = Object.keys(destinations).map((app) =>
    `<li><button class="hub-open" type="button" data-hub-open="${esc(app)}">`
    + `Open the ${esc(app)} rebuild</button></li>`).join('');
  return '<header class="hub-head"><h2>What Morph replaces</h2>'
    + `<p class="hub-thesis">The guard map could not be loaded (${esc(why)}). `
    + 'Every rebuild is still openable here; only its guard tally is missing.'
    + '</p></header>'
    + (doors ? `<ul class="hub-doors" aria-label="Rebuilds">${doors}</ul>` : '')
    + renderKept(kept);
}
